// ===========================================
// Lesson Attendance Button Component
// ===========================================
// Shows attendance status for a lesson and provides appropriate action
// - If attendance not marked: "Mark Attendance" button
// - If attendance marked: "View Attendance" button with summary

import { useState } from 'react';
import MarkedByCaption from './MarkedByCaption';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Skeleton,
  Divider,
  Alert,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useEnrolledStudentsForAttendance } from '../../hooks/useAttendance';
import {
  getAttendanceStatusColor,
  getAttendanceStatusLabel,
} from '../../api/attendance.api';
import AttendanceMarker from './AttendanceMarker';

// ===========================================
// TYPES
// ===========================================

interface LessonAttendanceButtonProps {
  lessonId: string;
  lessonName: string;
  date: Date;
  // When set (used for hybrid individual sessions), the attendance view and
  // marker are scoped to a single enrolled student rather than the full class.
  studentId?: string;
}

// ===========================================
// COMPONENT
// ===========================================

export default function LessonAttendanceButton({
  lessonId,
  lessonName,
  date,
  studentId,
}: LessonAttendanceButtonProps) {
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const dateStr = format(date, 'yyyy-MM-dd');

  // Fetch enrolled students with attendance status (narrowed by studentId when
  // supplied — hybrid individual sessions only have one enrolled student to mark).
  const { data, isLoading, refetch } = useEnrolledStudentsForAttendance(
    lessonId,
    dateStr,
    studentId
  );

  const students = data?.students || [];

  // Check if attendance has been marked (at least one student has attendance)
  const hasAttendance = students.some((s) => s.attendance);

  // Count attendance by status
  const attendanceCounts = students.reduce(
    (acc, student) => {
      if (student.attendance) {
        acc[student.attendance.status] = (acc[student.attendance.status] || 0) + 1;
        acc.total++;
      }
      return acc;
    },
    { total: 0 } as Record<string, number>
  );

  const handleViewClick = () => {
    setViewModalOpen(true);
  };

  const handleMarkClick = () => {
    setEditModalOpen(true);
  };

  const handleCloseView = () => {
    setViewModalOpen(false);
  };

  const handleCloseEdit = () => {
    setEditModalOpen(false);
    refetch(); // Refresh attendance data after editing
  };

  const handleEditFromView = () => {
    setViewModalOpen(false);
    setEditModalOpen(true);
  };

  if (isLoading) {
    return <Skeleton variant="rectangular" width={120} height={32} sx={{ borderRadius: 1 }} />;
  }

  return (
    <>
      {hasAttendance ? (
        <Button
          size="small"
          variant="contained"
          startIcon={<CheckIcon />}
          onClick={handleViewClick}
          sx={{
            bgcolor: 'rgba(69,128,228,0.15)',
            color: '#4580E4',
            boxShadow: 'none',
            '&:hover': { bgcolor: 'rgba(69,128,228,0.25)', boxShadow: 'none' },
          }}
        >
          Attendance
        </Button>
      ) : (
        <Button
          size="small"
          variant="outlined"
          startIcon={<CheckIcon />}
          onClick={handleMarkClick}
          sx={{
            borderColor: '#4580E4',
            color: '#4580E4',
            '&:hover': { borderColor: '#3a6fcc', bgcolor: 'rgba(69,128,228,0.08)' },
          }}
        >
          Attendance
        </Button>
      )}

      {/* View Attendance Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={handleCloseView}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6">Attendance Details</Typography>
              <Typography variant="body2" color="text.secondary">
                {lessonName} - {format(date, 'EEEE, d MMMM yyyy')}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseView}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* Summary */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {attendanceCounts.PRESENT > 0 && (
              <Chip
                label={`${attendanceCounts.PRESENT} Present`}
                color="success"
                size="small"
              />
            )}
            {attendanceCounts.ABSENT > 0 && (
              <Chip
                label={`${attendanceCounts.ABSENT} Absent`}
                color="error"
                size="small"
              />
            )}
            {attendanceCounts.LATE > 0 && (
              <Chip
                label={`${attendanceCounts.LATE} Late`}
                color="warning"
                size="small"
              />
            )}
            {attendanceCounts.EXCUSED > 0 && (
              <Chip
                label={`${attendanceCounts.EXCUSED} Excused`}
                color="info"
                size="small"
              />
            )}
            {attendanceCounts.CANCELLED > 0 && (
              <Chip
                label={`${attendanceCounts.CANCELLED} Cancelled`}
                color="default"
                size="small"
              />
            )}
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Student List */}
          {students.length === 0 ? (
            <Alert severity="info">No students enrolled in this lesson.</Alert>
          ) : (
            <List disablePadding>
              {students.map((student) => (
                <ListItem
                  key={student.id}
                  sx={{
                    py: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 0 },
                  }}
                >
                  <ListItemText
                    primary={`${student.firstName} ${student.lastName}`}
                  />
                  {student.attendance ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <Chip
                        label={getAttendanceStatusLabel(student.attendance.status)}
                        color={getAttendanceStatusColor(student.attendance.status)}
                        size="small"
                      />
                      {student.attendance.absenceReason && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          {student.attendance.absenceReason}
                        </Typography>
                      )}
                      <MarkedByCaption
                        markedByName={student.attendance.markedByName}
                        markedAt={student.attendance.markedAt}
                      />
                    </Box>
                  ) : (
                    <Chip
                      label="Not Marked"
                      color="default"
                      size="small"
                      variant="outlined"
                    />
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseView}>Close</Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEditFromView}
          >
            Edit Attendance
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit/Mark Attendance Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {hasAttendance ? 'Edit Attendance' : 'Mark Attendance'}
            </Typography>
            <IconButton onClick={handleCloseEdit}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <AttendanceMarker
            lessonId={lessonId}
            lessonName={lessonName}
            date={date}
            studentId={studentId}
            onClose={handleCloseEdit}
            onSaved={handleCloseEdit}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
